import React, { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from './logo.png';
import { logout } from "./utils/logout";
import html2pdf from 'html2pdf.js';
import './App.css'; 

const SavingsDashboard = ({ licenseData }) => {
  const navigate = useNavigate();
  const [licenseTypeFilter, setLicenseTypeFilter] = useState("All");
  const [viewMode, setViewMode] = useState("Annual");
  const [selectedKPI, setSelectedKPI] = useState("Total");

  const exportRef = useRef();

  const handleExportToPDF = () => {
    const element = exportRef.current;
    const clone = element.cloneNode(true);
  
    // Inject logo at top
    const logoDiv = document.createElement('div');
    logoDiv.style.textAlign = 'center';
    logoDiv.style.paddingBottom = '20px';
  
    const logoImg = document.createElement('img');
    logoImg.src = '/logo.png'; // Ensure this matches your public path
    logoImg.alt = 'Logo';
    logoImg.style.height = '80px';
  
    logoDiv.appendChild(logoImg);
    clone.prepend(logoDiv);
      
    // Hide export buttons
    const exportButtons = clone.querySelectorAll("button");
    exportButtons.forEach(btn => {
      if (btn.innerText.trim() === "Export to PDF") {
        btn.style.display = "none";
      }
    });
  
    // Normalize and center layout
    clone.classList.add('pdf-export-mode');
    clone.style.width = '700px';         // A4 width in pixels at 96 DPI
    clone.style.minHeight = '1000px';    // A4 height to fit content exactly
    clone.style.maxHeight = '1000px';
    clone.style.overflow = 'hidden';
    clone.style.margin = '0 auto';
    clone.style.padding = '10px';
    clone.style.fontFamily = 'Inter, sans-serif';
    clone.style.background = '#fff';
    clone.style.color = '#000';
      
    const opt = {
      margin: [15, 15],
      filename: 'PowerAudit - Cost Summary.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2.5,
        useCORS: true,
        scrollY: 0  
      },
      jsPDF: {
        unit: 'pt',
        format: 'a4',
        orientation: 'portrait'
      }
    };
  
    requestAnimationFrame(() => {
      html2pdf()
        .from(clone)
        .set(opt)
        .output('blob')
        .then(blob => {
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = 'PowerAudit - Cost Summary.pdf';
          link.click();
          URL.revokeObjectURL(link.href);
        });
    });
  };
  

  const filteredData = useMemo(() => {
    const pricedData = licenseData.filter(row =>
      (row["Assigned Cost (Monthly)"] || 0) > 0 ||
      (row["Unassigned Cost (Monthly)"] || 0) > 0 ||
      (row["Assigned Cost (Annual)"] || 0) > 0 ||
      (row["Unassigned Cost (Annual)"] || 0) > 0
    );

    return licenseTypeFilter === "All"
      ? pricedData
      : pricedData.filter(d => d["License Type"] === licenseTypeFilter);
  }, [licenseData, licenseTypeFilter]);

  const getValue = (row, type) => {
    const suffix = viewMode === "Quarterly" ? "Monthly" : viewMode;
    const multiplier = viewMode === "Quarterly" ? 3 : 1;
    return (row[`${type} Cost (${suffix})`] || 0) * multiplier;
  };

  const totalAssigned = filteredData.reduce((sum, row) => sum + getValue(row, "Assigned"), 0);
  const totalUnassigned = filteredData.reduce((sum, row) => sum + getValue(row, "Unassigned"), 0);
  const totalCost = totalAssigned + totalUnassigned;

  const sortedRows = [...filteredData].sort((a, b) => getValue(b, "Unassigned") - getValue(a, "Unassigned"));

  return (
    <div style={{ padding: '20px', width: '90%', maxWidth: '1250px', margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
      
      {/* Header with Logo and Logout */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px' }}>
        <img src={logo} alt="Logo" style={{ height: '80px' }} />
        <div className="no-print">
          <button
            onClick={() => logout(navigate)}
            style={{
              marginLeft: 'auto',
              backgroundColor: '#CD0029',
              color: 'white',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Export Button */}
      {viewMode === "Annual" && (
        <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <button
            onClick={handleExportToPDF}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              backgroundColor: '#02AD24',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
            }}
          >
            Export to PDF
          </button>
        </div>
      )}

      {/* Exportable content only below this line */}
      <div ref={exportRef}>
        {/* Filters */}
        <div style={{
          display: 'flex',
          gap: '24px',
          marginBottom: '32px',
          flexWrap: 'wrap',
          alignItems: 'flex-start'
        }}>
          <div>
            <label style={{ fontWeight: 500, fontSize: '14px', display: 'block', marginBottom: '6px' }}>License Type</label>
            <select
              value={licenseTypeFilter}
              onChange={e => setLicenseTypeFilter(e.target.value)}
              style={{
                padding: '10px 14px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                fontSize: '15px'
              }}
            >
              <option>All</option>
              {[...new Set(
                licenseData
                  .filter(row =>
                    (row["Assigned Cost (Monthly)"] || 0) > 0 ||
                    (row["Unassigned Cost (Monthly)"] || 0) > 0 ||
                    (row["Assigned Cost (Annual)"] || 0) > 0 ||
                    (row["Unassigned Cost (Annual)"] || 0) > 0
                  )
                  .map(d => d["Friendly Name"] || d["License Type"])
              )].map((type, i) => (
                <option key={i}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ fontWeight: 500, fontSize: '14px', display: 'block', marginBottom: '6px' }}>&nbsp;</label>
            <select
              value={viewMode}
              onChange={e => setViewMode(e.target.value)}
              style={{
                padding: '10px 14px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                fontSize: '15px'
              }}
            >
              <option>Annual</option>
              <option>Quarterly</option>
              <option>Monthly</option>
            </select>
          </div>
        </div>

        {/* KPI Cards */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '32px',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div onClick={() => setSelectedKPI("Assigned")} style={{
            flex: 1,
            background: selectedKPI === "Assigned" ? '#e9fbe8' : '#fff',
            border: selectedKPI === "Assigned" ? '2px solid #02AD24' : '2px solid transparent',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            minWidth: '180px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}>Assigned Cost</div>
            <div style={{ fontSize: '22px', fontWeight: 700 }}>${totalAssigned.toLocaleString()}</div>
          </div>

          <div onClick={() => setSelectedKPI("Sunk")} style={{
            flex: 1,
            background: selectedKPI === "Sunk" ? '#DA7B7B' : '#CD0029',
            border: selectedKPI === "Sunk" ? '2px solid #02AD24' : '2px solid transparent',
            color: 'white',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            minWidth: '180px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}>Sunk Cost</div>
            <div style={{ fontSize: '22px', fontWeight: 700 }}>${totalUnassigned.toLocaleString()}</div>
          </div>

          <div onClick={() => setSelectedKPI("Total")} style={{
            flex: 1,
            background: selectedKPI === "Total" ? '#e9fbe8' : '#fff',
            border: selectedKPI === "Total" ? '2px solid #02AD24' : '2px solid transparent',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            minWidth: '180px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '16px', fontWeight: 500, marginBottom: '8px' }}>Total Cost</div>
            <div style={{ fontSize: '22px', fontWeight: 700 }}>${totalCost.toLocaleString()}</div>
          </div>
        </div>

        {/* Table */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          padding: '32px',
          marginBottom: '40px'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', background: '#f5f5f5' }}>
                <th style={{ padding: '12px 16px' }}>License Type</th>
                {selectedKPI === "Assigned" && (
                  <>
                    <th style={{ padding: '12px 16px' }}>Assigned %</th>
                    <th style={{ padding: '12px 16px' }}>Assigned Cost</th>
                  </>
                )}
                {selectedKPI === "Sunk" && (
                  <>
                    <th style={{ padding: '12px 16px' }}>Unassigned %</th>
                    <th style={{ padding: '12px 16px' }}>Sunk Cost</th>
                  </>
                )}
                {selectedKPI === "Total" && (
                  <>
                    <th style={{ padding: '12px 16px' }}>Assigned %</th>
                    <th style={{ padding: '12px 16px' }}>Unassigned %</th>
                    <th style={{ padding: '12px 16px' }}>Total Cost</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, i) => {
                const assigned = row["Assigned Licenses"] || 0;
                const unassigned = row["Unassigned Licenses"] || 0;
                const total = assigned + unassigned || 1;

                const assignedPercent = Math.round((assigned / total) * 100);
                const unassignedPercent = 100 - assignedPercent;

                const assignedCost = getValue(row, "Assigned");
                const unassignedCost = getValue(row, "Unassigned");
                const totalCost = assignedCost + unassignedCost;

                return (
                  <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px 16px' }}>{row["Friendly Name"] || row["License Type"]}</td>
                    {selectedKPI === "Assigned" && (
                      <>
                        <td style={{ padding: '12px 16px' }}>{assignedPercent}%</td>
                        <td style={{ padding: '12px 16px' }}>${assignedCost.toLocaleString()}</td>
                      </>
                    )}
                    {selectedKPI === "Sunk" && (
                      <>
                        <td style={{ padding: '12px 16px' }}>{unassignedPercent}%</td>
                        <td style={{ padding: '12px 16px' }}>${unassignedCost.toLocaleString()}</td>
                      </>
                    )}
                    {selectedKPI === "Total" && (
                      <>
                        <td style={{ padding: '12px 16px' }}>{assignedPercent}%</td>
                        <td style={{ padding: '12px 16px' }}>{unassignedPercent}%</td>
                        <td style={{ padding: '12px 16px' }}>${totalCost.toLocaleString()}</td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Back Button (outside export scope) */}
      <div className="no-print" style={{ textAlign: 'center' }}>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            padding: '14px 28px',
            fontSize: '16px',
            backgroundColor: '#02AD24',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
          }}
        >
          Back to Main Dashboard
        </button>
      </div>
    </div>
  );
};

export default SavingsDashboard;
